import { App as AntApp, Avatar, Button, Space, Typography, Upload } from "antd";
import { CameraOutlined, UserOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, PHOTO_ACCEPT, looksAcceptable, multipart } from "../app/files.ts";
import { useApplyProfile, useReportError, type StudentProfile } from "../app/students.ts";

type PhotoForm = components["schemas"]["UploadStudentPhotoForm"];

const PHOTO_SIZE = 112;

/** The private profile photo, fetched through the API with the bearer token and shown from an object URL. */
export function StudentPhoto({ student, canManage }: { student: StudentProfile; canManage: boolean }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const applyProfile = useApplyProfile();
  const reportError = useReportError();
  const [version, setVersion] = useState(0);
  const [url, setUrl] = useState<string | null>(null);

  const photo = useQuery({
    queryKey: ["students", student.id, "photo", version],
    enabled: student.hasPhoto,
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/students/{id}/photo", { params: { path: { id: student.id } }, parseAs: "blob" });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });

  useEffect(() => {
    if (!photo.data) return;
    const objectUrl = URL.createObjectURL(photo.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [photo.data]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { error, response } = await api.PUT("/students/{id}/photo", {
        params: { path: { id: student.id } },
        body: multipart<PhotoForm>({ file }),
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      setVersion((v) => v + 1);
      await applyProfile({ ...student, hasPhoto: true });
      void message.success(t("student.photoSaved"));
    },
    onError: reportError,
  });

  function choose(file: File) {
    if (!looksAcceptable(file, true)) void message.error(t("student.documents.wrongType"));
    else if (file.size > MAX_UPLOAD_BYTES) void message.error(t("student.documents.tooLarge", { size: MAX_UPLOAD_MB }));
    else upload.mutate(file);
    return Upload.LIST_IGNORE;
  }

  const name = `${student.firstName} ${student.lastName}`;
  return (
    <Space orientation="vertical" align="center" size={8}>
      {url && student.hasPhoto ? (
        <Avatar size={PHOTO_SIZE} src={url} alt={t("student.photoAlt", { name })} shape="square" />
      ) : (
        <Avatar size={PHOTO_SIZE} icon={<UserOutlined />} shape="square" aria-label={t("student.noPhoto")} />
      )}
      {canManage && (
        <>
          <Upload accept={PHOTO_ACCEPT} showUploadList={false} beforeUpload={choose}>
            <Button size="small" icon={<CameraOutlined />} loading={upload.isPending}>
              {student.hasPhoto ? t("student.replacePhoto") : t("student.uploadPhoto")}
            </Button>
          </Upload>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("student.photoHint", { size: MAX_UPLOAD_MB })}
          </Typography.Text>
        </>
      )}
    </Space>
  );
}
